import PropTypes from 'prop-types';
import {
  Box, Typography, Alert, TablePagination, CircularProgress, useMediaQuery, useTheme,
} from '@mui/material';
import { History, Inbox, NorthEast, SouthWest } from '@mui/icons-material';
import WaCard from './WaCard';
import { softTints, slate } from '../../constants/uiTokens';

const DIRECTION_LABEL = { outbound: 'Enviada', inbound: 'Recebida' };
const PURPOSE_LABEL = {
  created: 'Agendamento criado',
  confirmation_request: 'Confirmação',
  reminder: 'Lembrete',
  cancelled: 'Cancelamento',
  reply: 'Resposta',
};
const STATUS_TINT = {
  sent: softTints.info,
  delivered: softTints.success,
  read: softTints.success,
  failed: softTints.error,
  undelivered: softTints.error,
  received: softTints.info,
};
const GRID_COLS = '150px 110px 1fr 1fr 140px';

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function StatusBadge({ status }) {
  const t = STATUS_TINT[status] || softTints.neutral;
  return (
    <Box component="span" sx={{ display: 'inline-block', fontSize: 12, fontWeight: 600, px: 1.25, py: '3px', borderRadius: 999, bgcolor: t.bg, color: t.fg }}>
      {status}
    </Box>
  );
}
StatusBadge.propTypes = { status: PropTypes.string };

function DirectionBadge({ direction }) {
  const out = direction === 'outbound';
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 600, px: 1, py: '2px', borderRadius: '6px', bgcolor: out ? softTints.info.bg : '#f3eefb', color: out ? softTints.info.fg : '#6b3fa0' }}>
      {out ? <NorthEast sx={{ fontSize: 14 }} /> : <SouthWest sx={{ fontSize: 14 }} />}
      {DIRECTION_LABEL[direction] ?? direction}
    </Box>
  );
}
DirectionBadge.propTypes = { direction: PropTypes.string };

/**
 * MessageHistoryCard — histórico de mensagens WhatsApp. Tabela no desktop, lista no
 * mobile, resumo no header e paginação. Traduzido do mockup; lê os campos reais
 * (createdAt/direction/purpose/status/phoneMasked) e o summary (sent/received/failedNoCredit).
 */
export default function MessageHistoryCard({
  messages, total, page, rowsPerPage,
  onPageChange, onRowsPerPageChange,
  loading, error, setError, summary,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isEmpty = !loading && total === 0 && !error;

  const summaryChips = summary && total > 0 ? (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {[
        { value: summary.sent, label: 'enviadas', color: '#1976d2' },
        { value: summary.received, label: 'recebidas', color: '#6b3fa0' },
        ...(summary.failedNoCredit > 0 ? [{ value: summary.failedNoCredit, label: 'sem saldo', color: '#d32f2f' }] : []),
      ].map((s) => (
        <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.875, bgcolor: softTints.neutral.bg, border: '1px solid', borderColor: '#eaeef1', borderRadius: '8px', px: 1.5, py: 0.875 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: s.color }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>{s.value ?? 0}</Typography>
          <Typography sx={{ fontSize: 11.5, color: slate[500] }}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  ) : null;

  return (
    <WaCard
      icon={<History sx={{ fontSize: 22, color: 'primary.dark' }} />}
      title="Histórico de mensagens"
      action={summaryChips}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      )}

      {!loading && isEmpty && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
          <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#f0f3f6', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.75 }}>
            <Inbox sx={{ fontSize: 32, color: '#aeb8c0' }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: slate[700], mb: 0.5 }}>Nenhuma mensagem ainda</Typography>
          <Typography sx={{ fontSize: 13, color: slate[500], maxWidth: 340 }}>
            Quando o WhatsApp enviar a primeira mensagem, ela aparece aqui com data, status e telefone.
          </Typography>
        </Box>
      )}

      {!loading && !isEmpty && !isMobile && (
        <Box sx={{ border: '1px solid', borderColor: '#eaeef1', borderRadius: '9px', overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, bgcolor: softTints.neutral.bg, borderBottom: '1px solid', borderColor: '#eaeef1', px: 2, py: 1.25, fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em', color: slate[500], textTransform: 'uppercase' }}>
            <Box>Data</Box><Box>Direção</Box><Box>Tipo</Box><Box>Status</Box><Box>Telefone</Box>
          </Box>
          {messages.map((m, idx) => (
            <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, px: 2, py: 1.5, borderBottom: idx < messages.length - 1 ? '1px solid' : 'none', borderColor: '#f2f4f6', alignItems: 'center', fontSize: 13, color: slate[700] }}>
              <Box sx={{ color: slate[600] }}>{formatDateTime(m.createdAt)}</Box>
              <Box><DirectionBadge direction={m.direction} /></Box>
              <Box>{PURPOSE_LABEL[m.purpose] ?? m.purpose ?? '—'}</Box>
              <Box><StatusBadge status={m.status} /></Box>
              <Box sx={{ fontVariantNumeric: 'tabular-nums', color: slate[600] }}>{m.phoneMasked}</Box>
            </Box>
          ))}
        </Box>
      )}

      {!loading && !isEmpty && isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {messages.map((m, idx) => (
            <Box key={idx} sx={{ border: '1px solid', borderColor: '#eaeef1', borderRadius: '9px', p: 1.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <DirectionBadge direction={m.direction} />
                <StatusBadge status={m.status} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>{PURPOSE_LABEL[m.purpose] ?? m.purpose ?? '—'}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, fontSize: 12.5, color: slate[500] }}>
                <Box component="span">{formatDateTime(m.createdAt)}</Box>
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{m.phoneMasked}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!loading && !isEmpty && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      )}
    </WaCard>
  );
}

MessageHistoryCard.propTypes = {
  messages: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  summary: PropTypes.object,
};
